import { useState, useEffect, useRef } from 'react';
import { Search, MapPin, X, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

const popularDestinations = [
  'Maldives', 'Switzerland', 'Bali', 'Dubai', 'Paris',
  'Singapore', 'Thailand', 'Kerala', 'Goa', 'Rajasthan'
];

export function SearchBar({ value, onChange, onSearch, className }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
  };

  const handleSearch = async () => {
    setShowSuggestions(false);
    setIsSearching(true);
    onChange(inputValue);
    await onSearch();
    setIsSearching(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (destination) => {
    setInputValue(destination);
    onChange(destination);
    setShowSuggestions(false);
    onSearch();
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    inputRef.current?.focus();
  };

  const filteredSuggestions = popularDestinations.filter(
    dest => dest.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A4A]" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search destinations, tours..."
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          className="pl-12 pr-24 h-14 rounded-xl bg-white text-[#1A1A1A] text-base border-gray-200 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
          data-testid="tours-search-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && (
            <button
              onClick={handleClear}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-[#4A4A4A]" />
            </button>
          )}
          <button
            onClick={handleSearch}
            disabled={isSearching}
            className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isSearching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Search'
            )}
          </button>
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (inputValue === '' || filteredSuggestions.length > 0) && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 z-20 overflow-hidden"
        >
          {inputValue === '' && (
            <div className="p-3 border-b border-gray-100">
              <span className="text-xs font-medium text-[#4A4A4A] uppercase tracking-wide">
                Popular Destinations
              </span>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {(inputValue === '' ? popularDestinations : filteredSuggestions).map((dest) => (
              <button
                key={dest}
                onClick={() => handleSuggestionClick(dest)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
              >
                <MapPin className="w-4 h-4 text-[#FF6B35]" />
                <span className="text-[#1A1A1A]">{dest}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
